
import { useState } from 'react';

const PDFs = () => {
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const folders = [
    { id: 'all', label: 'All Documents', count: 15 },
    { id: 'contracts', label: 'Contracts', count: 3 },
    { id: 'proposals', label: 'Proposals', count: 4 },
    { id: 'reports', label: 'Reports', count: 5 },
    { id: 'specifications', label: 'Specifications', count: 3 }
  ];

  const documents = [
    {
      id: 1,
      name: 'Project Proposal - TechCorp Website Redesign.pdf',
      folder: 'proposals',
      size: '2.4 MB',
      uploadDate: '2024-01-15',
      lastModified: '2024-01-15',
      status: 'Approved',
      description: 'Initial project proposal with scope, timeline, and budget'
    },
    {
      id: 2,
      name: 'Technical Specifications Document.pdf',
      folder: 'specifications',
      size: '1.8 MB',
      uploadDate: '2024-01-18',
      lastModified: '2024-01-20',
      status: 'Under Review',
      description: 'Detailed technical requirements and system specifications'
    },
    {
      id: 3,
      name: 'Design Requirements and Guidelines.pdf',
      folder: 'specifications',
      size: '3.2 MB',
      uploadDate: '2024-01-20',
      lastModified: '2024-01-22',
      status: 'Approved',
      description: 'Brand guidelines, color schemes, and design standards'
    },
    {
      id: 4,
      name: 'Project Contract - Signed.pdf',
      folder: 'contracts',
      size: '1.1 MB',
      uploadDate: '2024-01-22',
      lastModified: '2024-01-22',
      status: 'Signed',
      description: 'Fully executed project contract with terms and conditions'
    },
    {
      id: 5,
      name: 'Weekly Progress Report - Week 1.pdf',
      folder: 'reports',
      size: '856 KB',
      uploadDate: '2024-01-26',
      lastModified: '2024-01-26',
      status: 'Submitted',
      description: 'First week progress summary and milestone updates'
    },
    {
      id: 6,
      name: 'User Research Findings.pdf',
      folder: 'reports',
      size: '4.1 MB',
      uploadDate: '2024-01-28',
      lastModified: '2024-01-30',
      status: 'Final',
      description: 'Comprehensive user research analysis and recommendations'
    }
  ];

  const filteredDocuments = selectedFolder === 'all' 
    ? documents 
    : documents.filter(doc => doc.folder === selectedFolder);

  const getStatusColor = (status: string) => {
    const colors = {
      'Approved': 'bg-green-100 text-green-800',
      'Under Review': 'bg-yellow-100 text-yellow-800',
      'Signed': 'bg-blue-100 text-blue-800',
      'Submitted': 'bg-purple-100 text-purple-800',
      'Final': 'bg-gray-100 text-gray-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-gray-900">Project Documents</h3>
          <span className="text-sm text-gray-500">
            {filteredDocuments.length} {filteredDocuments.length === 1 ? 'document' : 'documents'}
          </span>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
          >
            <option value="date">Sort by Date</option>
            <option value="name">Sort by Name</option>
            <option value="size">Sort by Size</option>
            <option value="status">Sort by Status</option>
          </select>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap">
            <i className="ri-upload-line mr-2"></i>
            Upload PDF
          </button>
        </div>
      </div>

      {/* Folder Filter */}
      <div className="flex flex-wrap gap-2">
        {folders.map((folder) => (
          <button
            key={folder.id}
            onClick={() => setSelectedFolder(folder.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              selectedFolder === folder.id
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <i className="ri-folder-line mr-1"></i>
            {folder.label} ({folder.count})
          </button>
        ))}
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-5">Document Name</div>
            <div className="col-span-2">Size</div>
            <div className="col-span-2">Upload Date</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1">Actions</div>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {filteredDocuments.map((document) => (
            <div key={document.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-file-pdf-line text-red-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{document.name}</p>
                      <p className="text-xs text-gray-500">{document.description}</p>
                    </div>
                  </div>
                </div>
                <div className="col-span-2">
                  <span className="text-sm text-gray-900">{document.size}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-sm text-gray-900">{document.uploadDate}</span>
                </div>
                <div className="col-span-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(document.status)}`}>
                    {document.status}
                  </span>
                </div>
                <div className="col-span-1">
                  <div className="flex items-center space-x-1">
                    <button className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                      <i className="ri-eye-line text-sm"></i>
                    </button>
                    <button className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                      <i className="ri-download-line text-sm"></i>
                    </button>
                    <button className="p-1 text-gray-400 hover:text-red-600 rounded-md hover:bg-gray-100">
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {filteredDocuments.length === 0 && (
        <div className="text-center py-12">
          <i className="ri-file-pdf-line text-4xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
          <p className="text-gray-500 mb-4">Upload some PDF documents to get started</p>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap">
            <i className="ri-upload-line mr-2"></i>
            Upload First Document
          </button>
        </div>
      )}

      {/* Storage Usage */}
      <div className="bg-blue-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-blue-900">Storage Usage</h4>
          <span className="text-sm text-blue-700">24.8 MB / 100 MB</span>
        </div>
        <div className="w-full bg-blue-200 rounded-full h-2">
          <div className="bg-blue-600 h-2 rounded-full" style={{ width: '25%' }}></div>
        </div>
        <p className="text-xs text-blue-700 mt-1">75.2 MB remaining</p>
      </div>
    </div>
  );
};

export default PDFs;
